import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Copy,
  Eye,
  ExternalLink,
  Trash2,
  RefreshCcw,
  Sparkles,
  AlertTriangle,
  Users,
  MessageSquare,
} from "lucide-react";
import SEO from "@/components/SEO";
import {
  adminMe,
  setAdminToken,
  getAdminToken,
  adminBetaStats,
  adminBetaTesters,
  adminBetaFeedback,
  adminBetaCreateInvites,
  adminBetaResyncMailchimp,
  adminBetaRevoke,
} from "@/lib/api";
import { useSessionRenewal } from "@/hooks/useSessionRenewal";

const SEVERITY_COLORS = {
  1: "text-[#6B7280]",
  2: "text-[#374151]",
  3: "text-[#B45309]",
  4: "text-[#B91C1C]",
  5: "text-[#7F1D1D]",
};

export default function BetaAdminDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [testers, setTesters] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [csv, setCsv] = useState("");
  const [defaultTier, setDefaultTier] = useState("site_test");
  const [sendEmails, setSendEmails] = useState(true);
  const [filterTag, setFilterTag] = useState("");

  useEffect(() => {
    if (!getAdminToken()) {
      nav("/admin/login", { replace: true });
      return;
    }
    adminMe()
      .then((res) => {
        if (res?.expires_at) setAdminToken(getAdminToken(), res.expires_at);
      })
      .catch(() => {
        setAdminToken("");
        nav("/admin/login", { replace: true });
      });
  }, [nav]);

  useSessionRenewal({
    onExpired: () => {
      setAdminToken("");
      nav("/admin/login", { replace: true });
    },
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t, f] = await Promise.all([
        adminBetaStats(),
        adminBetaTesters(),
        adminBetaFeedback({ limit: 100 }),
      ]);
      setStats(s);
      setTesters(t.testers || []);
      setFeedback(f.feedback || []);
    } catch (err) {
      toast.error("Couldn't load beta data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const parseCsv = (raw) => {
    // Accept lines like:  "Jane Doe, jane@example.com, full_experience"
    // Tier is optional — defaults to `defaultTier`.
    const lines = raw
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    const out = [];
    for (const line of lines) {
      const parts = line.split(/,|\t/).map((p) => p.trim());
      if (parts.length < 2) continue;
      const [name, email, tierMaybe] = parts;
      if (!name || !email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) continue;
      const tier =
        tierMaybe && ["site_test", "full_experience"].includes(tierMaybe.toLowerCase())
          ? tierMaybe.toLowerCase()
          : defaultTier;
      out.push({ name, email: email.toLowerCase(), tier });
    }
    return out;
  };

  const onGenerate = async () => {
    const rows = parseCsv(csv);
    if (rows.length === 0) {
      toast.error("No valid rows found. Format: name, email, [tier].");
      return;
    }
    setBusy("invite");
    try {
      const res = await adminBetaCreateInvites(rows, sendEmails);
      const sentMsg = sendEmails ? " and emailed" : "";
      toast.success(
        `${res.created.length} invite(s) created${sentMsg}. ${res.skipped.length} skipped (duplicates).`,
      );
      if ((res.created || []).length > 0) {
        setCsv("");
        load();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't create invites.");
    } finally {
      setBusy("");
    }
  };

  const onRevoke = async (testerId, email) => {
    if (!window.confirm(`Revoke access for ${email}? They will not be able to log in again.`))
      return;
    setBusy(testerId);
    try {
      await adminBetaRevoke(testerId);
      toast.success("Access revoked.");
      load();
    } catch {
      toast.error("Couldn't revoke that tester.");
    } finally {
      setBusy("");
    }
  };

  const onResync = async () => {
    setBusy("resync");
    try {
      const res = await adminBetaResyncMailchimp();
      toast.success(`Synced ${res.synced} · failed ${res.failed} · total ${res.total}.`);
      load();
    } catch {
      toast.error("MailChimp resync failed. Check API key.");
    } finally {
      setBusy("");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code).then(
      () => toast.success(`Copied ${code}`),
      () => toast.error("Couldn't copy."),
    );
  };

  const filteredFeedback = filterTag
    ? feedback.filter((f) => (f.ai_tag || "").toLowerCase() === filterTag.toLowerCase())
    : feedback;

  const tagOptions = Array.from(
    new Set(feedback.map((f) => f.ai_tag).filter(Boolean)),
  );

  return (
    <main className="bg-white min-h-screen" data-testid="beta-admin">
      <SEO title="Beta Admin · Ashlan Clinic" path="/admin/beta" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <button
          type="button"
          onClick={() => nav("/admin")}
          className="ash-link-underline text-sm inline-flex items-center gap-2 mb-6"
        >
          <ArrowLeft size={14} /> Admin home
        </button>
        <div className="flex items-center gap-3 mb-2">
          <Sparkles size={20} className="text-[#3A7A8C]" />
          <span className="ash-eyebrow">Early Access Preview</span>
        </div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-[#080808]">
          Beta control room
        </h1>
        <p className="mt-3 text-[#3C4048] max-w-2xl">
          Generate invites, monitor testers, and review AI-classified feedback for the
          3 – 23 August 2026 preview window.
        </p>

        {/* Splash preview tools — for sharing the gate experience with the owner / stakeholders. */}
        <div
          className="mt-6 flex flex-wrap items-center gap-3 p-5 bg-[#F5F5F5] border-l-2 border-[#5FA8B4]"
          data-testid="beta-splash-tools"
        >
          <div className="flex-1 min-w-[220px]">
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#3A7A8C]">
              Preview the splash
            </p>
            <p className="mt-1 text-sm text-[#3C4048] leading-relaxed">
              See exactly what testers and visitors will see during the 3 – 23 August preview
              — works anytime, even outside the beta window.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              const url = "https://beta.ashlanclinic.com/?previewbeta=1";
              window.open(url, "_blank", "noopener");
            }}
            data-testid="beta-preview-open"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#5FA8B4] text-white font-bold uppercase tracking-[0.12em] text-xs hover:bg-[#3A7A8C] transition-colors"
          >
            <Eye size={14} /> Open Splash <ExternalLink size={12} />
          </button>
          <button
            type="button"
            onClick={async () => {
              const url = "https://beta.ashlanclinic.com/?previewbeta=1";
              try {
                await navigator.clipboard.writeText(url);
                toast.success("Preview link copied — paste it into an email or text.");
              } catch {
                toast.error("Couldn't copy automatically. Link: " + url);
              }
            }}
            data-testid="beta-preview-copy"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#e5e7eb] text-[#080808] font-bold uppercase tracking-[0.12em] text-xs hover:border-[#3A7A8C] transition-colors"
          >
            <Copy size={14} /> Copy Share Link
          </button>
        </div>

        {loading && (
          <p className="mt-10 text-[#3C4048] inline-flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </p>
        )}

        {!loading && stats && (
          <section
            className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4"
            data-testid="beta-stats"
          >
            <Stat label="Testers" value={`${stats.testers_total}/${stats.tester_cap}`} icon={Users} />
            <Stat label="Active" value={stats.testers_active} />
            <Stat label="Feedback (total)" value={stats.feedback_total} icon={MessageSquare} />
            <Stat
              label="Urgent (≥4)"
              value={stats.feedback_urgent}
              icon={AlertTriangle}
              urgent={stats.feedback_urgent > 0}
            />
          </section>
        )}

        {/* Invite generator */}
        <section className="mt-12">
          <h2 className="text-2xl font-bold tracking-tight text-[#080808]">
            1 · Generate invites
          </h2>
          <p className="mt-2 text-sm text-[#3C4048] max-w-2xl">
            Paste rows in <code>name, email[, tier]</code> format — one per line. Tier defaults to
            the selection below if omitted. Codes are auto-generated and emails are queued via
            Resend.
          </p>
          <div className="mt-5 grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <textarea
                value={csv}
                onChange={(e) => setCsv(e.target.value)}
                data-testid="beta-invite-csv"
                rows={8}
                placeholder={"Jane Doe, jane@example.com, site_test\nSam Test, sam@example.com, full_experience"}
                className="w-full px-4 py-3 border border-[#e5e7eb] focus:outline-none focus:border-[#3A7A8C] font-mono text-sm"
              />
            </div>
            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#080808] mb-2">
                  Default tier
                </label>
                <select
                  value={defaultTier}
                  onChange={(e) => setDefaultTier(e.target.value)}
                  data-testid="beta-invite-tier"
                  className="w-full px-4 py-3 border border-[#e5e7eb] focus:outline-none focus:border-[#3A7A8C]"
                >
                  <option value="site_test">Site Tester</option>
                  <option value="full_experience">Full Experience</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-[#3C4048]">
                <input
                  type="checkbox"
                  checked={sendEmails}
                  onChange={(e) => setSendEmails(e.target.checked)}
                  data-testid="beta-send-emails"
                />
                Auto-email invite to each tester
              </label>
              <button
                type="button"
                onClick={onGenerate}
                disabled={busy === "invite"}
                data-testid="beta-invite-generate"
                className="ash-btn-primary disabled:opacity-60"
              >
                {busy === "invite" ? "Generating…" : "Generate & send"}
              </button>
              <button
                type="button"
                onClick={onResync}
                disabled={busy === "resync"}
                data-testid="beta-resync-mc"
                className="ash-btn-secondary text-sm"
              >
                <RefreshCcw size={14} />{" "}
                {busy === "resync" ? "Syncing…" : "Resync to MailChimp"}
              </button>
            </div>
          </div>
        </section>

        {/* Tester list */}
        <section className="mt-16">
          <h2 className="text-2xl font-bold tracking-tight text-[#080808]">
            2 · Tester list ({testers.length})
          </h2>
          <div className="mt-5 overflow-x-auto" data-testid="beta-testers">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-[0.18em] text-[#6B7280] border-b border-[#e5e7eb]">
                <tr>
                  <th className="py-3 pr-4">Name</th>
                  <th className="py-3 pr-4">Email</th>
                  <th className="py-3 pr-4">Tier</th>
                  <th className="py-3 pr-4">Code</th>
                  <th className="py-3 pr-4">Status</th>
                  <th className="py-3 pr-4">Feedback</th>
                  <th className="py-3 pr-4">MC</th>
                  <th className="py-3 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {testers.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-6 text-[#6B7280] italic">
                      No testers yet — paste a row above to invite the first.
                    </td>
                  </tr>
                )}
                {testers.map((t) => (
                  <tr key={t.id} className="border-b border-[#f1f1f1]">
                    <td className="py-3 pr-4 text-[#080808] font-medium">{t.name}</td>
                    <td className="py-3 pr-4 text-[#3C4048]">{t.email}</td>
                    <td className="py-3 pr-4">
                      <span
                        className={`text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-1 ${
                          t.tier === "full_experience"
                            ? "bg-[#3A7A8C]/10 text-[#3A7A8C]"
                            : "bg-[#F5F5F5] text-[#3C4048]"
                        }`}
                      >
                        {t.tier === "full_experience" ? "Full Experience" : "Site"}
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      <button
                        type="button"
                        onClick={() => copyCode(t.code)}
                        data-testid={`copy-code-${t.code}`}
                        className="inline-flex items-center gap-1.5 font-mono text-xs text-[#080808] hover:text-[#3A7A8C]"
                      >
                        {t.code} <Copy size={12} />
                      </button>
                    </td>
                    <td className="py-3 pr-4 text-[#3C4048]">{t.status}</td>
                    <td className="py-3 pr-4 text-[#3C4048]">{t.feedback_count || 0}</td>
                    <td className="py-3 pr-4">
                      {t.mailchimp_synced ? (
                        <span className="text-xs text-[#15803D]">✓</span>
                      ) : (
                        <span className="text-xs text-[#6B7280]">—</span>
                      )}
                    </td>
                    <td className="py-3 pr-4">
                      {!t.revoked && (
                        <button
                          type="button"
                          onClick={() => onRevoke(t.id, t.email)}
                          disabled={busy === t.id}
                          data-testid={`revoke-${t.id}`}
                          className="text-[#B91C1C] hover:text-[#7F1D1D]"
                          aria-label={`Revoke ${t.email}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Feedback inbox */}
        <section className="mt-16">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h2 className="text-2xl font-bold tracking-tight text-[#080808]">
              3 · Feedback inbox ({filteredFeedback.length})
            </h2>
            {tagOptions.length > 0 && (
              <select
                value={filterTag}
                onChange={(e) => setFilterTag(e.target.value)}
                data-testid="beta-feedback-filter"
                className="px-3 py-2 border border-[#e5e7eb] text-sm"
              >
                <option value="">All tags</option>
                {tagOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="mt-5 grid gap-4" data-testid="beta-feedback-list">
            {filteredFeedback.length === 0 && (
              <p className="text-[#6B7280] italic text-sm">
                No feedback yet — invite testers above and they&apos;ll appear here as soon as
                they submit.
              </p>
            )}
            {filteredFeedback.map((f) => (
              <article
                key={f.id}
                className={`border-l-2 ${
                  f.ai_severity >= 4 ? "border-[#B91C1C] bg-[#FEF2F2]" : "border-[#5FA8B4] bg-white"
                } border border-[#e5e7eb] p-5`}
                data-testid={`beta-feedback-row-${f.id}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <p className="font-semibold text-[#080808]">{f.tester_name}</p>
                    <span className="text-xs text-[#6B7280]">{f.tester_email}</span>
                    <span
                      className={`text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-0.5 ${
                        f.tier === "full_experience"
                          ? "bg-[#3A7A8C]/10 text-[#3A7A8C]"
                          : "bg-[#F5F5F5] text-[#3C4048]"
                      }`}
                    >
                      {f.tier === "full_experience" ? "Full" : "Site"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    {f.ai_tag && (
                      <span className="px-2 py-0.5 bg-[#080808] text-white uppercase tracking-[0.14em]">
                        {f.ai_tag}
                      </span>
                    )}
                    {typeof f.ai_severity === "number" && (
                      <span
                        className={`font-mono ${
                          SEVERITY_COLORS[f.ai_severity] || "text-[#6B7280]"
                        }`}
                      >
                        sev {f.ai_severity}/5
                      </span>
                    )}
                    {typeof f.rating === "number" && (
                      <span className="text-[#3A7A8C]">★ {f.rating}/5</span>
                    )}
                  </div>
                </div>
                {f.ai_summary && (
                  <p className="mt-3 text-[#080808] font-semibold leading-snug">
                    {f.ai_summary}
                  </p>
                )}
                {f.ai_action && (
                  <p className="mt-1.5 text-sm text-[#3C4048]">
                    <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#3A7A8C] mr-2">
                      Action
                    </span>
                    {f.ai_action}
                  </p>
                )}
                <details className="mt-3">
                  <summary className="cursor-pointer text-xs text-[#3A7A8C] hover:text-[#2c5f6e]">
                    View transcript ({(f.messages || []).length} turns)
                  </summary>
                  <div className="mt-3 bg-[#F5F5F5] p-4 text-sm text-[#3C4048] space-y-2">
                    {(f.messages || []).map((m, i) => (
                      <p key={i}>
                        <span
                          className={`text-[10px] uppercase tracking-[0.18em] font-semibold mr-2 ${
                            m.role === "user" ? "text-[#3A7A8C]" : "text-[#6B7280]"
                          }`}
                        >
                          {m.role === "user" ? "Tester" : "Concierge"}
                        </span>
                        {m.content}
                      </p>
                    ))}
                  </div>
                </details>
                <p className="mt-3 text-[11px] text-[#6B7280]">
                  {f.page_url || "/"} · {new Date(f.created_at).toLocaleString()}
                </p>
              </article>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}

function Stat({ label, value, icon: Icon, urgent }) {
  return (
    <div
      className={`p-5 border ${
        urgent ? "border-[#B91C1C] bg-[#FEF2F2]" : "border-[#e5e7eb] bg-white"
      }`}
    >
      <div className="flex items-center gap-2">
        {Icon && (
          <Icon
            size={14}
            className={urgent ? "text-[#B91C1C]" : "text-[#3A7A8C]"}
            aria-hidden="true"
          />
        )}
        <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#6B7280]">
          {label}
        </span>
      </div>
      <p
        className={`mt-2 text-3xl font-bold ${
          urgent ? "text-[#B91C1C]" : "text-[#080808]"
        }`}
      >
        {value}
      </p>
    </div>
  );
}
